import { useMemo } from "react";
import { cn } from "@/lib/utils";
import { valueToText } from "./shared";

type Op = { type: "eq" | "del" | "add"; text: string };
type Row = { left?: { text: string; changed: boolean }; right?: { text: string; changed: boolean } };

const MAX_CELLS = 250_000; // LCS table cap; beyond this fall back to "everything changed"

/** Line-level LCS diff. */
export function diffLines(a: string[], b: string[]): Op[] {
  // Trim common prefix/suffix first: keeps the DP table small for typical edits.
  let start = 0;
  while (start < a.length && start < b.length && a[start] === b[start]) start++;
  let endA = a.length;
  let endB = b.length;
  while (endA > start && endB > start && a[endA - 1] === b[endB - 1]) {
    endA--;
    endB--;
  }
  const head: Op[] = a.slice(0, start).map((text) => ({ type: "eq", text }));
  const tail: Op[] = a.slice(endA).map((text) => ({ type: "eq", text }));
  const ma = a.slice(start, endA);
  const mb = b.slice(start, endB);
  const n = ma.length;
  const m = mb.length;

  let mid: Op[];
  if (n * m > MAX_CELLS) {
    mid = [...ma.map((text) => ({ type: "del" as const, text })), ...mb.map((text) => ({ type: "add" as const, text }))];
  } else {
    const w = m + 1;
    const dp = new Uint32Array((n + 1) * w);
    for (let i = n - 1; i >= 0; i--) {
      for (let j = m - 1; j >= 0; j--) {
        dp[i * w + j] = ma[i] === mb[j] ? dp[(i + 1) * w + j + 1] + 1 : Math.max(dp[(i + 1) * w + j], dp[i * w + j + 1]);
      }
    }
    mid = [];
    let i = 0;
    let j = 0;
    while (i < n && j < m) {
      if (ma[i] === mb[j]) {
        mid.push({ type: "eq", text: ma[i] });
        i++;
        j++;
      } else if (dp[(i + 1) * w + j] >= dp[i * w + j + 1]) {
        mid.push({ type: "del", text: ma[i++] });
      } else {
        mid.push({ type: "add", text: mb[j++] });
      }
    }
    while (i < n) mid.push({ type: "del", text: ma[i++] });
    while (j < m) mid.push({ type: "add", text: mb[j++] });
  }
  return [...head, ...mid, ...tail];
}

/** Pair deletions and additions of one change block side by side. */
function toRows(ops: Op[]): Row[] {
  const rows: Row[] = [];
  let k = 0;
  while (k < ops.length) {
    const op = ops[k];
    if (op.type === "eq") {
      rows.push({ left: { text: op.text, changed: false }, right: { text: op.text, changed: false } });
      k++;
      continue;
    }
    const dels: string[] = [];
    const adds: string[] = [];
    while (k < ops.length && ops[k].type !== "eq") {
      (ops[k].type === "del" ? dels : adds).push(ops[k].text);
      k++;
    }
    for (let r = 0; r < Math.max(dels.length, adds.length); r++) {
      rows.push({
        left: r < dels.length ? { text: dels[r], changed: true } : undefined,
        right: r < adds.length ? { text: adds[r], changed: true } : undefined,
      });
    }
  }
  return rows;
}

function Cell({ cell, side }: { cell?: { text: string; changed: boolean }; side: "left" | "right" }) {
  return (
    <div
      className={cn(
        "min-h-[1.4em] whitespace-pre-wrap break-words px-2 py-px",
        !cell && "bg-sl-grey-50",
        cell?.changed && side === "left" && "bg-[#FDECEC] text-[#7A0A10]",
        cell?.changed && side === "right" && "bg-[#EEF6E4] text-[#2B4C0B]",
      )}
    >
      {cell ? (
        <>
          {cell.changed && <span className="mr-1 select-none opacity-60">{side === "left" ? "−" : "+"}</span>}
          {cell.text || " "}
        </>
      ) : null}
    </div>
  );
}

/** Side-by-side before/after view with removed lines red and added lines green. */
export function SideBySideDiff({ before, after, className }: { before: unknown; after: unknown; className?: string }) {
  const beforeText = valueToText(before);
  const afterText = valueToText(after);
  const rows = useMemo(() => {
    const a = beforeText === "" ? [] : beforeText.split("\n");
    const b = afterText === "" ? [] : afterText.split("\n");
    return toRows(diffLines(a, b));
  }, [beforeText, afterText]);

  return (
    <div className={cn("overflow-hidden rounded-md border border-border font-mono text-xs leading-relaxed", className)}>
      <div className="grid grid-cols-2 border-b border-border bg-sl-grey-50 text-[11px] font-sans font-semibold uppercase tracking-wide text-muted-foreground">
        <div className="border-r border-border px-2 py-1.5">Voor</div>
        <div className="px-2 py-1.5">Na</div>
      </div>
      {rows.length === 0 ? (
        <div className="px-2 py-2 font-sans text-muted-foreground">(leeg)</div>
      ) : (
        <div className="max-h-96 overflow-auto">
          {rows.map((r, i) => (
            <div key={i} className="grid grid-cols-2">
              <div className="border-r border-border">
                <Cell cell={r.left} side="left" />
              </div>
              <Cell cell={r.right} side="right" />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Word-level and item-level diffs (readable for prose, JSON and lists)
// ---------------------------------------------------------------------------

type Tok = { type: "eq" | "del" | "add"; text: string };

/** Token-level LCS diff (words, whitespace and punctuation as separate tokens). */
export function diffWords(a: string, b: string): Tok[] {
  const split = (s: string) => s.match(/\s+|[\wÀ-ɏ-]+|[^\s\w]/g) ?? [];
  return diffLines(split(a), split(b)) as Tok[];
}

const DEL = "rounded-sm bg-[#FBD9DB] text-[#7A0A10] line-through decoration-[#7A0A10]/60";
const ADD = "rounded-sm bg-[#D7EBC2] text-[#1F3A06]";

function parseJson(s: string): unknown {
  const t = s.trim();
  if (!t || !"[{".includes(t[0])) return undefined;
  try {
    return JSON.parse(t);
  } catch {
    return repairJson(t);
  }
}

/**
 * The diff API truncates long values (600 chars), which breaks JSON. Close the open string and brackets so the
 * readable part can still be shown structurally; the last item is marked with "…".
 */
function repairJson(t: string): unknown {
  const s = t.replace(/(\.{3}|…)$/, "");
  const stack: string[] = [];
  let inStr = false;
  let esc = false;
  for (const ch of s) {
    if (inStr) {
      if (esc) esc = false;
      else if (ch === "\\") esc = true;
      else if (ch === '"') inStr = false;
      continue;
    }
    if (ch === '"') inStr = true;
    else if (ch === "{" || ch === "[") stack.push(ch);
    else if (ch === "}" || ch === "]") stack.pop();
  }
  let out = s;
  if (inStr) out += '…"';
  out = out.replace(/[,:]\s*$/, "").replace(/,\s*"[^"]*"\s*$/, (m) => (stack[stack.length - 1] === "{" ? "" : m));
  for (let i = stack.length - 1; i >= 0; i--) out += stack[i] === "{" ? "}" : "]";
  try {
    return JSON.parse(out);
  } catch {
    return undefined;
  }
}

const isStrArray = (v: unknown): v is string[] => Array.isArray(v) && v.every((x) => typeof x === "string" || typeof x === "number");

function asText(v: unknown): string {
  if (typeof v === "string") {
    const p = parseJson(v);
    return p !== undefined ? JSON.stringify(p, null, 2) : v;
  }
  return valueToText(v);
}

type Run = { type: "eq"; text: string } | { type: "chg"; del: string; add: string };

/** Group token ops into runs: whitespace between two changes joins the change, so edits read as phrases. */
function toRuns(toks: Tok[]): Run[] {
  const runs: Run[] = [];
  let i = 0;
  while (i < toks.length) {
    if (toks[i].type === "eq") {
      runs.push({ type: "eq", text: toks[i].text });
      i++;
      continue;
    }
    let del = "";
    let add = "";
    while (i < toks.length) {
      const t = toks[i];
      if (t.type === "del") del += t.text;
      else if (t.type === "add") add += t.text;
      else if (/^\s+$/.test(t.text) && i + 1 < toks.length && toks[i + 1].type !== "eq") {
        del += t.text;
        add += t.text;
      } else break;
      i++;
    }
    runs.push({ type: "chg", del, add });
  }
  return runs;
}

function similarity(toks: Tok[]): number {
  let eq = 0;
  let all = 0;
  toks.forEach((t) => {
    if (/^\s+$/.test(t.text)) return;
    all++;
    if (t.type === "eq") eq++;
  });
  return all ? eq / all : 1;
}

function WordDiffInline({ toks, side }: { toks: Tok[]; side?: "left" | "right" }) {
  const runs = useMemo(() => toRuns(toks), [toks]);
  return (
    <span className="whitespace-pre-wrap break-words">
      {runs.map((r, i) => {
        if (r.type === "eq") return <span key={i}>{r.text}</span>;
        return (
          <span key={i}>
            {side !== "right" && r.del.trim() && (
              <del className={DEL}>
                <span className="sr-only">verwijderd: </span>
                {r.del}
              </del>
            )}
            {side !== "left" && side !== "right" && r.del.trim() && r.add.trim() && " "}
            {side !== "left" && r.add.trim() && (
              <ins className={cn(ADD, "no-underline")}>
                <span className="sr-only">toegevoegd: </span>
                {r.add}
              </ins>
            )}
          </span>
        );
      })}
    </span>
  );
}

function ItemDiff({ before, after }: { before: string[]; after: string[] }) {
  const b = new Set(before.map(String));
  const a = new Set(after.map(String));
  const removed = [...b].filter((x) => !a.has(x));
  const added = [...a].filter((x) => !b.has(x));
  const kept = [...a].filter((x) => b.has(x));
  return (
    <div className="space-y-2 text-[13px]">
      {added.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="w-24 shrink-0 text-xs font-semibold text-success">+ {added.length} toegevoegd</span>
          {added.map((x) => (
            <ins key={x} className={cn(ADD, "rounded-md px-2 py-0.5 no-underline")}>
              {x}
            </ins>
          ))}
        </div>
      )}
      {removed.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="w-24 shrink-0 text-xs font-semibold text-destructive">− {removed.length} verwijderd</span>
          {removed.map((x) => (
            <del key={x} className={cn(DEL, "rounded-md px-2 py-0.5")}>
              {x}
            </del>
          ))}
        </div>
      )}
      {kept.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 text-muted-foreground">
          <span className="w-24 shrink-0 text-xs font-medium">{kept.length} ongewijzigd</span>
          {kept.map((x) => (
            <span key={x} className="rounded-md border border-border px-2 py-0.5">
              {x}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export type DiffMode = "inline" | "split";

/**
 * Best readable diff for a before/after pair: list items for string arrays, word-level highlights for prose
 * and (pretty-printed) JSON. `mode` switches between one merged text and two columns.
 */
export function SmartDiff({ before, after, mode = "inline", className }: { before: unknown; after: unknown; mode?: DiffMode; className?: string }) {
  const pb = typeof before === "string" ? parseJson(before) : before;
  const pa = typeof after === "string" ? parseJson(after) : after;
  const blank = (v: unknown) => v === null || v === undefined || v === "" || v === "null";
  if ((structured(pb) || blank(before)) && (structured(pa) || blank(after)) && (structured(pb) || structured(pa))) {
    const empty = (o: unknown) => (Array.isArray(o) ? [] : {});
    return (
      <div className={cn("rounded-md border border-border bg-card px-3 py-2.5", className)}>
        <StructuredDiff before={structured(pb) ? pb : empty(pa)} after={structured(pa) ? pa : empty(pb)} mode={mode} />
      </div>
    );
  }
  if (isStrArray(pb ?? []) && isStrArray(pa ?? []) && (Array.isArray(pb) || Array.isArray(pa))) {
    return (
      <div className={cn("rounded-md border border-border bg-card p-3", className)}>
        <ItemDiff before={(pb as string[]) ?? []} after={(pa as string[]) ?? []} />
      </div>
    );
  }
  const bt = asText(before);
  const at = asText(after);
  if (!/\s/.test(bt + at) && bt.length < 80 && at.length < 80) {
    return (
      <div className={cn("rounded-md border border-border bg-card px-3 py-2", className)}>
        <ScalarChange before={bt || undefined} after={at || undefined} />
      </div>
    );
  }
  return <TextDiff bt={bt} at={at} json={pb !== undefined || pa !== undefined} mode={mode} className={className} />;
}

function TextDiff({ bt, at, json, mode, className }: { bt: string; at: string; json: boolean; mode: DiffMode; className?: string }) {
  const toks = useMemo(() => diffWords(bt, at), [bt, at]);
  // Mostly rewritten text: interleaved word highlights are noise; show old and new as whole blocks.
  const rewritten = similarity(toks) < 0.4;
  if (rewritten) mode = "split";
  const body = cn("px-3 py-2.5 leading-relaxed", json ? "font-mono text-xs" : "text-sm");
  if (mode === "split") {
    return (
      <div className={cn("grid grid-cols-1 overflow-hidden rounded-md border border-border bg-card md:grid-cols-2", className)}>
        <div className="border-b border-border md:border-b-0 md:border-r">
          <div className="border-b border-border bg-sl-grey-50 px-3 py-1 text-xs font-semibold text-muted-foreground">Voor{rewritten && " · grotendeels herschreven"}</div>
          <div className={cn(body, "max-h-96 overflow-auto")}>{!bt ? <span className="text-muted-foreground">(leeg)</span> : rewritten ? <del className={cn(DEL, "no-underline")} style={{ textDecoration: "none" }}>{bt}</del> : <WordDiffInline toks={toks} side="left" />}</div>
        </div>
        <div>
          <div className="border-b border-border bg-sl-grey-50 px-3 py-1 text-xs font-semibold text-muted-foreground">Na</div>
          <div className={cn(body, "max-h-96 overflow-auto")}>{!at ? <span className="text-muted-foreground">(leeg)</span> : rewritten ? <ins className={cn(ADD, "no-underline")}>{at}</ins> : <WordDiffInline toks={toks} side="right" />}</div>
        </div>
      </div>
    );
  }
  return (
    <div className={cn("max-h-[28rem] overflow-auto rounded-md border border-border bg-card", body, className)}>
      {!bt && !at ? <span className="text-muted-foreground">(leeg)</span> : <WordDiffInline toks={toks} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Typed diff of parsed JSON: only changed properties, each in its natural form.
// ---------------------------------------------------------------------------

type J = Record<string, unknown>;
const isJObj = (v: unknown): v is J => typeof v === "object" && v !== null && !Array.isArray(v);
const structured = (v: unknown) => isJObj(v) || (Array.isArray(v) && v.length > 0 && v.every((x) => isJObj(x) || typeof x === "string"));
/** `{text: ...}` items (caveats, pitfalls) compare by their text. */
const itemText = (x: unknown) => (typeof x === "string" ? x : isJObj(x) && typeof x.text === "string" ? x.text : JSON.stringify(x));
const humanKey = (k: string) => {
  const s = k.replace(/[_-]+/g, " ").trim();
  return s.charAt(0).toUpperCase() + s.slice(1);
};

function ScalarChange({ before, after }: { before: unknown; after: unknown }) {
  const show = (v: unknown) => (v === undefined ? "(geen)" : v === null ? "(leeg)" : typeof v === "string" ? v : JSON.stringify(v));
  return (
    <span className="text-sm">
      <del className={cn(DEL, "px-1")}>{show(before)}</del> <span className="text-muted-foreground">→</span> <ins className={cn(ADD, "px-1 no-underline")}>{show(after)}</ins>
    </span>
  );
}

function StructuredDiff({ before, after, mode, depth = 0 }: { before: unknown; after: unknown; mode: DiffMode; depth?: number }) {
  if (Array.isArray(before) || Array.isArray(after)) {
    const b = Array.isArray(before) ? before : [];
    const a = Array.isArray(after) ? after : [];
    if ([...a, ...b].every((x) => typeof x === "string" || typeof x === "number" || (isJObj(x) && typeof x.text === "string"))) {
      return <ItemDiff before={b.map(itemText)} after={a.map(itemText)} />;
    }
    return <TextDiff bt={JSON.stringify(b, null, 2)} at={JSON.stringify(a, null, 2)} json mode={mode} />;
  }
  if (!isJObj(before) || !isJObj(after)) {
    if (typeof before === "string" && typeof after === "string" && /\s/.test(before + after)) return <TextDiff bt={before} at={after} json={false} mode={mode} />;
    return <ScalarChange before={before} after={after} />;
  }
  const keys = [...new Set([...Object.keys(before), ...Object.keys(after)])];
  const changed = keys.filter((k) => JSON.stringify(before[k]) !== JSON.stringify(after[k]));
  const same = keys.length - changed.length;
  if (!changed.length) return <span className="text-sm text-muted-foreground">Geen inhoudelijke verschillen.</span>;
  return (
    <div className="space-y-2.5">
      {changed.map((k) => {
        const b = before[k];
        const a = after[k];
        const added = b === undefined;
        const removed = a === undefined;
        return (
          <div key={k} className={cn(depth > 0 && "border-l-2 border-border pl-3")}>
            <div className="mb-1 flex items-center gap-2 text-[13px] font-semibold">
              <span>{humanKey(k)}</span>
              {added && <span className="text-xs font-medium text-success">nieuw</span>}
              {removed && <span className="text-xs font-medium text-destructive">verwijderd</span>}
            </div>
            {typeof b === "string" && typeof a === "string" && /\s/.test(a + b) ? (
              <div className="text-sm leading-relaxed">
                <TextDiff bt={b} at={a} json={false} mode={mode} className="border-0 p-0" />
              </div>
            ) : depth < 2 && (isJObj(a) || isJObj(b) || Array.isArray(a) || Array.isArray(b)) ? (
              <StructuredDiff before={b ?? (Array.isArray(a) ? [] : {})} after={a ?? (Array.isArray(b) ? [] : {})} mode={mode} depth={depth + 1} />
            ) : (
              <ScalarChange before={b} after={a} />
            )}
          </div>
        );
      })}
      {same > 0 && <p className="text-xs text-muted-foreground">{same} ongewijzigde velden verborgen.</p>}
    </div>
  );
}
